import React from 'react';
import { Marker, OverlayView } from '@react-google-maps/api';
import { isValidMapPosition } from './locationHelper';

export { isValidMapPosition };

function googleMapsReady() {
  return (
    typeof window !== 'undefined' &&
    !!window.google?.maps?.SymbolPath &&
    !!window.google?.maps?.OverlayView
  );
}

const PICKUP_BUBBLE_W = 80;
const PICKUP_BUBBLE_H = 36;
const ARROW_H = 8;

/**
 * Original baseline pickup/dropoff: HTML bubble via OverlayView with text label and an arrow
 * that points at the GPS point. Pickup bubble sits above the point (arrow ↓), dropoff below
 * (arrow ↑). Vehicle marker stays a rotated arrow via Marker for performance.
 */
const CustomMarker = ({
  position,
  type,
  text,
  orientation = 0,
  isWheelchair = false,
  vehicleStatus = 'Clear',
  hasWheelchairTrip = false,
}) => {
  if (!googleMapsReady() || !isValidMapPosition(position)) {
    return null;
  }

  const t = String(type || '').toLowerCase();
  if (t !== 'vehicle' && t !== 'pickup' && t !== 'dropoff') {
    return null;
  }

  const lat = Number(position.lat);
  const lng = Number(position.lng);
  const posLiteral = { lat, lng };
  const rot = Number(orientation);
  const rotation = Number.isFinite(rot) ? rot : 0;

  let vehicleColor = '#16a34a';
  switch (vehicleStatus) {
    case 'Clear':
      vehicleColor = '#16a34a';
      break;
    case 'Busy':
      vehicleColor = '#374151';
      break;
    case 'Loaded':
    case 'Enroute':
    case 'Dispatched':
      vehicleColor = hasWheelchairTrip ? '#42BAF0' : '#FFD205';
      break;
    default:
      vehicleColor = '#16a34a';
  }

  if (t === 'vehicle') {
    return (
      <Marker
        key={`veh-${lat.toFixed(6)}-${lng.toFixed(6)}-${rotation.toFixed(0)}`}
        position={posLiteral}
        icon={{
          path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
          scale: 5,
          fillColor: vehicleColor,
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2,
          rotation,
        }}
      />
    );
  }

  const fillColor = isWheelchair ? '#42BAF0' : '#FFD205';
  const labelColor = isWheelchair ? '#ffffff' : '#000000';
  const isPickup = t === 'pickup';

  // Pickup: bubble above the GPS point, arrow pointing down to the point.
  // Dropoff: bubble below the GPS point, arrow pointing up to the point.
  const getPixelPositionOffset = (offsetWidth, offsetHeight) => {
    const x = -offsetWidth / 2;
    if (isPickup) {
      return { x, y: -(offsetHeight) };
    }
    return { x, y: 0 };
  };

  const bubbleStyle = {
    width: PICKUP_BUBBLE_W,
    height: PICKUP_BUBBLE_H,
    background: fillColor,
    color: labelColor,
    border: '2px solid #ffffff',
    borderRadius: 6,
    boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: 12,
    fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    padding: '0 6px',
    pointerEvents: 'none',
    userSelect: 'none',
  };

  const arrowCommonStyle = {
    width: 0,
    height: 0,
    margin: '0 auto',
    borderLeft: `${ARROW_H}px solid transparent`,
    borderRight: `${ARROW_H}px solid transparent`,
    pointerEvents: 'none',
  };

  const arrowDown = {
    ...arrowCommonStyle,
    borderTop: `${ARROW_H}px solid #ffffff`,
    marginTop: -1,
  };
  const arrowUp = {
    ...arrowCommonStyle,
    borderBottom: `${ARROW_H}px solid #ffffff`,
    marginBottom: -1,
  };

  const wrapperStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    transform: 'translate(-50%, 0)', // OverlayView ignores native transforms; offset via getPixelPositionOffset
  };

  return (
    <OverlayView
      key={`wp-${String(text)}-${lat.toFixed(6)}-${lng.toFixed(6)}-${isPickup ? 'p' : 'd'}`}
      position={posLiteral}
      // FLOAT_PANE avoids the mouse-target pane init path that was throwing
      // "can't access property pH, X is undefined" when the overlay remounted
      // during a waypoint swap while panes were mid-update. The bubble has
      // pointerEvents: 'none' so it does not need mouse-target handling.
      mapPaneName={OverlayView.FLOAT_PANE}
      getPixelPositionOffset={getPixelPositionOffset}
    >
      <div style={wrapperStyle}>
        {!isPickup && <div style={arrowUp} />}
        <div style={bubbleStyle}>{String(text || '')}</div>
        {isPickup && <div style={arrowDown} />}
      </div>
    </OverlayView>
  );
};

export default CustomMarker;
